import TextButton from 'LuxKit/Button/TextButton'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { PrimaryNotificationDot } from 'tripPlanner/components/NotificationDot'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import { useUserSettings } from 'tripPlanner/hooks/api/userSettings'
import { useAppSelector } from 'hooks/reduxHooks'
import { useWishlistIndex } from 'wishlists/hooks'
import useWishlistReleaseState from 'wishlists/hooks/useWishlistReleaseState'

const MenuItem = styled.div`
  position: relative;
`

const TextWithNotification = styled.div`
  position: relative;
`

const MyTripNotificationDot = styled(PrimaryNotificationDot)`
  position: absolute;
  top: 0;
  left: 100%;
`

type InheritedTextButtonProps = Pick<ComponentProps<typeof TextButton>, 'size'>

interface Props extends InheritedTextButtonProps {}

function NavigationHeaderTripsMenu(props: Props) {
  const { size } = props
  const intl = useIntl()
  const { shouldUseWishlists } = useWishlistReleaseState()
  const { data: wishlistData } = useWishlistIndex()
  const { data: trips = [] } = useTrips()
  const { data: userSettings } = useUserSettings()
  const tripItemHasJustBeenAdded = useAppSelector(state => state.tripPlanner.tripItemHasJustBeenAdded)

  const userHasBeenAddedToNewTrips = !!userSettings?.recentlyAddedToTripIDs?.length

  if (shouldUseWishlists) {
    const hasSavedItems = Boolean(
      wishlistData?.wishlists.find((wishlist) => wishlist.isDefault)?.itemCount,
    )

    const wishlistText = intl.formatMessage({
      id: '8tn_OvJmXrdKVNnw4EQO',
      defaultMessage: 'Wishlist',
      description: 'Wishlist navigation button text in the website header',
    })

    return (
      <MenuItem>
        <TextButton
          kind="tertiary"
          variant="dark"
          size={size}
          startIcon={hasSavedItems ? <SolidHeartIcon colour="favourite" /> : <LineHeartIcon />}
          to="/wishlists"
        >
          {wishlistText}
        </TextButton>
      </MenuItem>
    )
  }

  const hasSavedItems = !!trips.find(trip => trip.itemCount > 0)
  const showTripNotificationDot = userHasBeenAddedToNewTrips || tripItemHasJustBeenAdded

  const tripPlansText = intl.formatMessage({
    id: '14ZdHAC4TaWCKVF3-lQD',
    defaultMessage: 'Trip plans',
    description: 'Heading for Trip Plans section in the trip planner feature',
  })

  return (
    <MenuItem>
      <TextButton
        kind="tertiary"
        variant="dark"
        size={size}
        startIcon={hasSavedItems ? <SolidHeartIcon colour="favourite" /> : <LineHeartIcon />}
        to="/trip-planner"
      >
        <TextWithNotification>
          {tripPlansText}
          {showTripNotificationDot && <MyTripNotificationDot />}
        </TextWithNotification>
      </TextButton>
    </MenuItem>
  )
}

export default NavigationHeaderTripsMenu
